import { Link } from "@tanstack/react-router";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect, useState, type ComponentType } from "react";
import { useCore } from "../lib/core";
import { getSubSidebarCollapsed, setSubSidebarCollapsed } from "../lib/sidebar-state";

/** One navigation entry. Exactly one of `to` (route) / `onClick` (action) is set. */
export type SideNavItem = {
  key: string;
  label: string;
  icon?: ComponentType<{ size?: number; className?: string }>;
  /** Route item → <Link>. */
  to?: string;
  /** Action item → <button>. */
  onClick?: () => void;
  /**
   * Whether this item is the current one. The CALLER computes it (route
   * consumers via `useLocation().pathname.startsWith(to)`, state consumers via
   * their selected tab). SideNav intentionally does NOT read router state, so
   * it can be hosted outside a TanStack RouterProvider — e.g. the standalone
   * Mademind console, which has no router. (Calling `useLocation()` here crashed
   * Mademind with "Cannot read properties of null (reading 'stores')".)
   */
  active?: boolean;
};

/**
 * The sub-sidebar (C3F v18): a LIGHT surface — bg-subtle, not the Night rail
 * palette; only the icon rail is always-Night. 224px expanded / 56px collapsed
 * (w-56 / w-14), persisted collapse, 48px header with the area name as a mono
 * eyebrow. Items: 40px, 8px radius (nav-item role), active = elevated surface
 * with default ink, inactive = muted ink; hover speaks through the background.
 *
 * Collapse needs icons to stay legible, so it is only offered when EVERY
 * item has an icon; otherwise the rail stays expanded and the toggle is hidden.
 */
export function SideNav({
  title,
  items,
  collapsible = true,
}: {
  title: string;
  items: ReadonlyArray<SideNavItem>;
  collapsible?: boolean;
}) {
  const core = useCore();
  const [collapsed, setCollapsed] = useState(getSubSidebarCollapsed());

  useEffect(() => {
    const handler = () => setCollapsed(getSubSidebarCollapsed());
    window.addEventListener("praesto:sidebar-toggle", handler);
    return () => window.removeEventListener("praesto:sidebar-toggle", handler);
  }, []);

  const canCollapse = collapsible && items.length > 0 && items.every((i) => i.icon);
  const isCollapsed = canCollapse && collapsed;
  const width = isCollapsed ? "w-14" : "w-56";

  const isItemActive = (item: SideNavItem): boolean => !!item.active;

  const railClass = (active: boolean): string =>
    `flex items-center gap-2.5 min-h-10 rounded-sm text-sm transition-colors duration-fast ${
      isCollapsed ? "justify-center px-0" : "px-2.5"
    } ${
      active
        ? "bg-elevated font-semibold text-fg-default"
        : "font-medium text-fg-muted hover:bg-hover"
    }`;

  const stripClass = (active: boolean): string =>
    `flex items-center text-sm font-medium min-h-touch ${
      active ? "text-fg-strong border-b-2 border-fg-strong" : "text-fg-muted"
    }`;

  const renderItem = (item: SideNavItem, cls: string, showLabel: boolean) => {
    const Icon = item.icon;
    const inner = (
      <>
        {Icon && <Icon size={17} className="flex-shrink-0" />}
        {showLabel && <span className="truncate">{item.label}</span>}
      </>
    );
    return item.to ? (
      <Link
        key={item.key}
        to={item.to as never}
        title={item.label}
        aria-label={item.label}
        aria-current={isItemActive(item) ? "page" : undefined}
        className={cls}
      >
        {inner}
      </Link>
    ) : (
      <button
        key={item.key}
        type="button"
        onClick={item.onClick}
        title={item.label}
        aria-label={item.label}
        aria-current={isItemActive(item) ? "page" : undefined}
        className={`w-full text-left ${cls}`}
      >
        {inner}
      </button>
    );
  };

  return (
    <>
      {/* Desktop sub-sidebar */}
      <aside
        className={`${width} hidden md:flex flex-col flex-shrink-0 border-r border-border-default bg-subtle transition-all`}
      >
        <div className="flex min-h-[48px] items-center justify-between border-b border-border-default px-2.5 py-0">
          {!isCollapsed && <h2 className="text-eyebrow truncate">{title}</h2>}
          {canCollapse && (
            <button
              type="button"
              onClick={() => setSubSidebarCollapsed(!collapsed)}
              className={`flex h-[30px] w-[30px] flex-shrink-0 items-center justify-center rounded-sm text-fg-muted transition-colors duration-fast hover:bg-hover ${
                isCollapsed ? "mx-auto" : "ml-auto"
              }`}
              aria-label={
                isCollapsed
                  ? core.i18n.t("shell.sidebar.expand")
                  : core.i18n.t("shell.sidebar.collapse")
              }
            >
              {isCollapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
            </button>
          )}
        </div>
        <nav className="grid flex-1 content-start gap-0.5 overflow-y-auto p-2">
          {items.map((item) => renderItem(item, railClass(isItemActive(item)), !isCollapsed))}
        </nav>
      </aside>

      {/* Mobile strip — shown below the shell's desktop breakpoint (md), so the
          rail/strip seam aligns with AppLayout's 768px desktop/mobile switch. */}
      <nav className="md:hidden border-b border-border-default overflow-x-auto no-scrollbar">
        <div className="flex gap-4 px-4 whitespace-nowrap">
          {items.map((item) => renderItem(item, stripClass(isItemActive(item)), true))}
        </div>
      </nav>
    </>
  );
}
